import { atom } from "jotai";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createHashRouter,
  redirect,
} from "react-router-dom";
import MedicalTravel from "./routes/MedicalTravel";
import AddMore from "./routes/AddMore";
import ByBoth from "./routes/ByBoth";
import ByPrivate from "./routes/ByPrivate";
import ByPublic from "./routes/ByPublic";
import ContactDetails from "./routes/ContactDetails";
import OtherUploads from "./routes/OtherUploads";
import TravelDetails from "./routes/TravelDetails";
import Summary from "./routes/Summary";
import ThankYou from "./routes/ThankYou";
import Fail from "./routes/SendFailed";
import { MedicalTravelModel } from "./types/FormTypes";
import Root from "./routes/root";
import Translate from "./routes/Translate";
import AddCertificate from "./routes/AddCertificate";
import RefundInfo from "./routes/RefundInfo";
require("./extensions/yup-extensions");

/*
    Store - Collecting FormData
*/
export const formDataAtom = atom<MedicalTravelModel>({
  compensationfor: "",
  patientssn: "",
  patientfirstname: "",
  patientlastname: "",
  patientemail: "",
  patientphone: "",
  caregiverssn: "",
  caregiverfirstname: "",
  caregiverphone: "",
  caregiverlastname: "",
  caregiveremail: "",
  clearingNumber: "",
  accountNumber: "",
  medicalVisits: [],
});

// Flow guard and router setup
const FLOW_KEY = "medical-travel-flow";

const navigationEntry = performance.getEntriesByType("navigation")[0] as
  | PerformanceNavigationTiming
  | undefined;

const isReload = navigationEntry?.type === "reload";

if (isReload) {
  sessionStorage.removeItem(FLOW_KEY);
}

function startFlow() {
  sessionStorage.setItem(FLOW_KEY, "active");
  return null;
}

function isFlowActive() {
  return sessionStorage.getItem(FLOW_KEY) === "active";
}

function clearFlow() {
  sessionStorage.removeItem(FLOW_KEY);
  return null;
}

function requireActiveFlow() {
  return isFlowActive() ? null : redirect("/");
}

function createProtectedStep(path: string, element: JSX.Element) {
  return {
    path,
    element,
    loader: async () => {
      return requireActiveFlow();
    },
  };
}

const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <MedicalTravel />,
        loader: () => {
          return startFlow();
        },
      },

      createProtectedStep("kontaktuppgifter", <ContactDetails />),
      createProtectedStep("utbetalning", <RefundInfo />),

      {
        path: "reseuppgifter/:id?",
        element: <Outlet />,
        loader: async () => {
          return requireActiveFlow();
        },
        children: [
          {
            index: true,
            element: <Navigate to="/reseuppgifter/intyg" replace={true} />,
          },
          {
            path: "intyg",
            element: <AddCertificate />,
          },
          {
            path: "om-resan",
            element: <TravelDetails />,
          },
          {
            path: "bil",
            element: <ByPrivate />,
          },
          {
            path: "kollektivtrafik",
            element: <ByPublic />,
          },
          {
            path: "bil-och-kollektivtrafik",
            element: <ByBoth />,
          },
          {
            path: "kostnader-for-overnattning",
            element: <OtherUploads />,
          },
          {
            path: "fler-resor",
            element: <AddMore />,
          },
        ],
      },

      createProtectedStep("granska-och-skicka", <Summary />),

      {
        path: "tack-sida",
        element: <ThankYou />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
      {
        path: "ett-fel-intraffade",
        element: <Fail />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
      {
        path: "oversatt",
        element: <Translate />,
      },
    ],
  },
]);

// Count pages
const pagePaths =
  router?.routes[0]?.children?.map((child) => child.path).filter(Boolean) || [];

export const pageCountAtom = atom({
  pagePaths,
});

function App() {
  return <RouterProvider router={router} />;
}

export default App;
